import {
  Checkbox,
  ChoiceGroup,
  DatePicker,
  Dropdown,
  DropdownMenuItemType,
  IChoiceGroupOption,
  IDropdownOption,
  ITheme,
  IconButton,
  Label,
  Link,
  Pivot,
  PivotItem,
  Separator,
  Stack,
  TextField,
  createTheme,
} from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import React, { useState } from 'react';
import {
  formatDate,
  parseAutoCompleteValue,
  allowsImageAndVideo,
  parseLatLng,
  useOtherInfo,
  type IValidationError,
} from '@moko/utils';
import { getAutoCompleteValues } from '../services/referenceService';
import { IField, IFieldsTab, IMetadata } from '../services/metadataService';
import {
  columnProps,
  labelColumnStyle,
  valueColumnStyle,
} from './styles/FormsStyles';
import { errorMessageAtom } from '../atoms/messageBarAtoms';
import { useSetAtom } from 'jotai';
import {
  AutocompleteSearchBox,
  HighlightTextView,
  ISuggestionItem,
  RenderIf,
} from '../libs';
import { FileSelector } from './FileSelector';
import { Link as FluentLink } from '@fluentui/react-components';
import {
  EllipsisFileName,
  FileViewerDialog,
  getFileNameFromUrl,
  getFileTypeIcon,
  getFileViewerUrl,
  MapPickDialog,
  type ILocation,
} from '@moko/ui';
import UserOrGroupSelector from './selectors/UsersOrGroupSelectors';
import AddressPicker from './AddressPicker';

const FileViewerLink = ({ field }: { field: IField }) => {
  const [open, setOpen] = useState(false);
  const url = getFileViewerUrl(field.options);
  if (!url) return null;
  const fileName = getFileNameFromUrl(url);
  return (
    <>
      <FluentLink
        as="button"
        type="button"
        onClick={() => setOpen(true)}
        style={{
          ...valueColumnStyle,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
          minHeight: 32,
          flexWrap: 'nowrap',
        }}
      >
        <span style={{ flexShrink: 0, display: 'flex' }}>
          {getFileTypeIcon(fileName)}
        </span>
        <EllipsisFileName fileName={fileName} />
      </FluentLink>
      <FileViewerDialog
        open={open}
        url={url}
        title={field.label}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export interface IOtherInfoProps {
  metadata: IMetadata;
  otherInfo: string;
  isSaved: React.MutableRefObject<boolean>;
  validationErrors?: IValidationError[];
  onOtherInfoChanged: (newValue: any) => void;
  onValidationErrorsChanged?: (newErrors: IValidationError[]) => unknown;
  relatedEntities?: { id: number; entityInfo: string }[];
  hideTitle?: boolean;
}

export function cleanOtherInfo(
  otherInfoObj: any,
  otherInfoFields?: IField[]
): string {
  const newOtherInfoObj: any = {};
  otherInfoFields?.forEach((field) => {
    newOtherInfoObj[field.name as keyof typeof newOtherInfoObj] =
      otherInfoObj[field.name];
  });
  otherInfoFields
    ?.filter((field) => field.fieldType === 'NumberWithUnit')
    .forEach((field) => {
      newOtherInfoObj[(field.name + '_Unit') as keyof typeof newOtherInfoObj] =
        otherInfoObj[field.name + '_Unit'];
    });
  otherInfoFields
    ?.filter((field) => field.fieldType === 'Contact')
    .forEach((field) => {
      newOtherInfoObj[(field.name + '_Ext') as keyof typeof newOtherInfoObj] =
        otherInfoObj[field.name + '_Ext'];
    });
  return JSON.stringify(newOtherInfoObj);
}

const OtherInfo = (params: IOtherInfoProps) => {
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const { otherInfoObj, handleOtherInfoChange: handleOtherInfoChangeBase } =
    useOtherInfo({
      metadata: params.metadata,
      otherInfo: params.otherInfo,
      isSaved: params.isSaved,
      validationErrors: params.validationErrors,
      onOtherInfoChanged: params.onOtherInfoChanged,
      onValidationErrorsChanged: params.onValidationErrorsChanged,
      relatedEntities: params.relatedEntities,
    });

  const getLookupOption = (
    metadata: IMetadata,
    field: IField
  ): IDropdownOption<any>[] => {
    const lookup = metadata.lookups.filter(
      (l) => l.name === field.lookupList
    )[0];
    if (lookup) {
      const lookupValues = lookup.values;
      return lookupValues.map((v) => ({
        key: v.key,
        text: v.value,
        itemType: v.header ? DropdownMenuItemType.Header : undefined,
      }));
    }
    return [];
  };

  const getChoiceGroupOption = (
    metadata: IMetadata,
    field: IField
  ): IChoiceGroupOption[] => {
    const lookup = metadata.lookups.filter(
      (l) => l.name === field.lookupList
    )[0];
    if (lookup) {
      const lookupValues = lookup.values;
      console.log(lookupValues);
      return lookupValues.map((v) => ({
        key: v.key,
        text: v.value,
      }));
    }
    return [];
  };

  const handleOtherInfoChange = (
    fieldName: string,
    value?: string,
    fieldType?: string
  ) => {
    if (fieldType === 'Number' && value !== undefined) {
      handleOtherInfoChangeBase(fieldName, `${Number.parseFloat(value)}`);
      return;
    }
    handleOtherInfoChangeBase(fieldName, value);
  };

  class refValue implements ISuggestionItem {
    constructor(public key: string, private value: string) {}
    getSearchText: () => string = () => {
      return this.value;
    };
    getSuggestionItem(query?: string) {
      return (
        <div key={this.key} className="suggestionItem">
          <div className="suggestionTitleRow row">
            <p className="suggestionTitle col-8">
              <HighlightTextView
                text={this.value}
                filter={query || ''}
              ></HighlightTextView>
            </p>
          </div>
        </div>
      );
    }
  }

  const [suggestions, setSuggestions] = useState<Map<string, refValue[]>>(
    new Map<string, refValue[]>()
  );
  const [selectedSuggestion, setSelectedSuggestion] = useState<
    Map<string, string>
  >(new Map<string, string>());
  const [searchBoxInProgress, setSearchBoxInProgress] = useState(false);

  const [currentField, setCurrentField] = useState<IField>();
  const [isMapPickOpen, { setTrue: showMapPick, setFalse: hideMapPick }] =
    useBoolean(false);

  const [
    isUserOrGroupSelectorOpen,
    { setTrue: showUserOrGroupSelector, setFalse: hideUserOrGroupSelector },
  ] = useBoolean(false);

  const theme: ITheme = createTheme();

  const createField = (field: IField) => (
    <Stack
      horizontal
      id={'field_' + field.name}
      style={field.hiden ? { display: 'none' } : undefined}
      verticalAlign={field.fieldType !== 'RadioButton' ? 'center' : 'baseline'}
    >
      {field.fieldType !== 'Panel' && (
        <Label style={labelColumnStyle}>{field.label}</Label>
      )}
      {field.options?.showAsLink ? (
        <Link
          href={field.options.linkUrl?.replace(
            '{id}',
            otherInfoObj[field.name]
          )}
          style={valueColumnStyle}
        >
          {otherInfoObj[field.name]}
        </Link>
      ) : (
        <RenderIf
          condition={
            field.fieldType === 'Number' ||
            field.fieldType === 'String' ||
            field.fieldType === 'Password'
          }
        >
          <TextField
            style={{ width: valueColumnStyle.width - 2 }}
            onChange={(_, newValue) =>
              handleOtherInfoChange(field.name, newValue, field.fieldType)
            }
            type={
              field.fieldType === 'Number'
                ? 'number'
                : field.fieldType === 'Password'
                ? 'password'
                : 'string'
            }
            description={field.hint ? field.hint : undefined}
            multiline={!!field.rows}
            rows={field.rows}
            value={otherInfoObj[field.name]}
            required={field.mandatory}
            errorMessage={
              params.validationErrors?.find((e) => e.fieldName === field.name)
                ?.message
            }
            readOnly={params.isSaved.current || field.readonly}
            borderless={params.isSaved.current || field.readonly}
          />
        </RenderIf>
      )}
      <RenderIf condition={field.fieldType === 'Checkbox'}>
        <Checkbox
          // style={valueColumnStyle}
          checked={otherInfoObj[field.name] === 'true'}
          required={field.mandatory}
          onChange={(_, isChecked) => {
            let value = isChecked?.toString();
            handleOtherInfoChange(field.name, value);
          }}
          disabled={params.isSaved.current || field.readonly}
        />
      </RenderIf>
      <RenderIf condition={field.fieldType === 'Lookup'}>
        <Dropdown
          style={valueColumnStyle}
          options={getLookupOption(params.metadata, field)}
          required={field.mandatory}
          multiSelect={field.multiSelect}
          selectedKey={field.multiSelect ? undefined : otherInfoObj[field.name]}
          selectedKeys={
            !!field.multiSelect
              ? (otherInfoObj[field.name] ?? '|').split('|')
              : undefined
          }
          onChange={(_, option) => {
            let value = option?.key.toString();
            if (field.multiSelect) {
              const currKeys: string[] = (otherInfoObj[field.name] ?? '|')
                .split('|')
                .filter((part: string) => part);
              if (option?.selected) {
                currKeys.push(option?.key.toString());
                value = currKeys.join('|');
              } else {
                value = currKeys
                  .filter((part: string) => part !== value)
                  .join('|');
              }
            }
            handleOtherInfoChange(field.name, value);
            if (!field.multiSelect) {
              const newRefValue = new refValue(
                option?.key as string,
                option?.text ?? ''
              );
              if (selectedSuggestion !== undefined) {
                const selection = new Map(selectedSuggestion);
                selection?.set(field.name, `${newRefValue.key}`);
                setSelectedSuggestion(selection);
              }
            }
          }}
          errorMessage={
            params.validationErrors?.find((e) => e.fieldName === field.name)
              ?.message
          }
          disabled={params.isSaved.current || field.readonly}
        />
      </RenderIf>
      <RenderIf condition={field.fieldType === 'RadioButton'}>
        <>
          <ChoiceGroup
            style={valueColumnStyle}
            options={getChoiceGroupOption(params.metadata, field)}
            required={field.mandatory}
            selectedKey={String(otherInfoObj[field.name])}
            onChange={(_, option) => {
              let value = option?.key;
              handleOtherInfoChange(field.name, value);
            }}
            disabled={params.isSaved.current || field.readonly}
          />
        </>
      </RenderIf>
      <RenderIf condition={field.fieldType === 'Image'}>
        <FileSelector
          value={otherInfoObj[field.name]}
          purpose="OtherInfo"
          imageSource={otherInfoObj[`${field.name}_src`] ?? ''}
          uploadImage={true}
          accept={
            allowsImageAndVideo(field.options) ? 'image/*,video/*' : 'image/*'
          }
          onUpload={(_id: number, url: string) =>
            handleOtherInfoChange(field.name, url)
          }
        />
      </RenderIf>
      <RenderIf condition={field.fieldType === 'AutoComplete'}>
        <AutocompleteSearchBox
          style={valueColumnStyle}
          id={field.name}
          placeholder={
            parseAutoCompleteValue(otherInfoObj[field.name])?.display ?? ''
          }
          suggestions={suggestions.get(field.name)}
          inProgress={searchBoxInProgress}
          onClear={() => {
            if (selectedSuggestion !== undefined) {
              const selection = new Map(selectedSuggestion);
              selection?.delete(field.name);
              setSelectedSuggestion(selection);
            }
          }}
          onChange={(_, newText) => {
            if (!newText || newText.trim() === '') {
              const newSuggestions = new Map(suggestions);
              newSuggestions?.delete(field.name);
              setSuggestions(newSuggestions);
            } else {
              const dependeeValue = field.dependees
                ? selectedSuggestion?.get(field.dependees ?? '')
                : undefined;
              const dependeeRef = params.metadata.fields.find(
                (f) => f.name === field.dependees
              )?.reference;
              const abortController = new AbortController();
              setSearchBoxInProgress(true);
              getAutoCompleteValues(
                abortController,
                field.reference ?? '',
                newText,
                `${dependeeRef}=${dependeeValue}`
              )
                .then((data: { key: string; value: string }[]) => {
                  const newSuggestions = new Map(suggestions);
                  newSuggestions.set(
                    field.name,
                    data.map((kv) => new refValue(kv.key, kv.value))
                  );
                  setSuggestions(newSuggestions);
                })
                .catch((e: any) => setErrorMessage(e))
                .finally(() => setSearchBoxInProgress(false));
            }
          }}
          onSuggestionClicked={(newValue: string | ISuggestionItem) => {
            const newRefValue = newValue as refValue;
            if (selectedSuggestion !== undefined) {
              const selection = new Map(selectedSuggestion);
              selection?.set(field.name, newRefValue.key ?? '');
              setSelectedSuggestion(selection);
              handleOtherInfoChange(
                field.name,
                `${newRefValue.key}:${newRefValue.getSearchText()}`
              );
            }
          }}
        />
      </RenderIf>
      <RenderIf condition={field.fieldType === 'Date'}>
        <DatePicker
          styles={{ root: { height: 32 } }}
          style={valueColumnStyle}
          placeholder={
            otherInfoObj[field.name]
              ? formatDate(
                  String(otherInfoObj[field.name]),
                  !!(
                    field.options?.showTime ||
                    field.options?.supportTimeInDateField
                  )
                )
              : ''
          }
          ariaLabel="Select a date"
          isMonthPickerVisible={false}
          onSelectDate={(selectedDate: Date | null | undefined) => {
            handleOtherInfoChange(field.name, selectedDate?.toISOString());
          }}
          disabled={params.isSaved.current || field.readonly}
        />
      </RenderIf>
      <RenderIf condition={field.fieldType === 'Coordinate'}>
        <div
          style={{
            position: 'relative',
            width: valueColumnStyle.width,
            marginLeft: 0,
            height: 32,
          }}
        >
          <TextField
            styles={{
              field: { height: 32 },
            }}
            value={otherInfoObj[field.name]}
            description={field.hint ? field.hint : undefined}
          />
          <IconButton
            iconProps={{
              iconName: 'Nav2DMapView',
            }}
            styles={{
              root: {
                position: 'absolute',
                top: 2,
                right: 2,
                height: 28,
              },
            }}
            onClick={() => {
              setCurrentField(field);
              showMapPick();
            }}
          />
        </div>
      </RenderIf>
      <RenderIf condition={field.fieldType === 'NumberWithUnit'}>
        <Stack horizontal style={valueColumnStyle}>
          <TextField
            description={field.hint ? field.hint : undefined}
            styles={{ root: { width: '48%' } }}
            value={otherInfoObj[field.name]}
            onChange={(_, newValue) =>
              handleOtherInfoChange(field.name, newValue)
            }
          />
          <span style={{ width: '2%' }} />
          <Dropdown
            styles={{ root: { width: '50%' } }}
            options={getLookupOption(params.metadata, field)}
            selectedKey={otherInfoObj[field.name + '_Unit']}
            onChange={(_, option) => {
              let value = option?.key.toString();
              handleOtherInfoChange(field.name + '_Unit', value);
            }}
            errorMessage={
              params.validationErrors?.find((e) => e.fieldName === field.name)
                ?.message
            }
            disabled={params.isSaved.current}
          />
        </Stack>
      </RenderIf>
      <RenderIf condition={field.fieldType === 'UserGroupSelector'}>
        <TextField
          description={field.hint ? field.hint : undefined}
          style={valueColumnStyle}
          value={otherInfoObj[field.name]}
          readOnly
          onClick={showUserOrGroupSelector}
        />
        <IconButton
          iconProps={{ iconName: 'ProfileSearch' }}
          styles={{
            root: {
              position: 'relative',
              top: 2,
              right: 34,
              height: 28,
            },
          }}
          onClick={() => showUserOrGroupSelector()}
        />
        <UserOrGroupSelector
          userOrGroupOnly={'user'}
          isOpen={isUserOrGroupSelectorOpen}
          fromRefUserValues={true}
          onUserOrGroupSelected={(
            id: number,
            isGroup: boolean,
            username?: string
          ) => {
            handleOtherInfoChange(
              field.name,
              `${isGroup ? 'G' : 'U'}${id.toString()}|${username}`,
              field.fieldType
            );
          }}
          hideUsersAndGroupsSelector={hideUserOrGroupSelector}
        />
      </RenderIf>
      <RenderIf condition={field.fieldType === 'Address'}>
        <AddressPicker
          style={{ width: valueColumnStyle.width - 2 }}
          onPlaceSelect={(place) => {
            handleOtherInfoChange(
              field.name,
              place ? place.formatted_address : '',
              field.fieldType
            );
          }}
          value={otherInfoObj[field.name]}
        />
      </RenderIf>
      <RenderIf condition={field.fieldType === 'FileViewer'}>
        <FileViewerLink field={field} />
      </RenderIf>
      <RenderIf condition={field.fieldType === 'Panel'}>
        <div
          style={{
            width: valueColumnStyle.width + labelColumnStyle.width,
            paddingLeft: 10,
            backgroundColor: theme.palette.themeLighter,
          }}
        >
          {field.label}
        </div>
      </RenderIf>
    </Stack>
  );

  return (
    <Stack {...columnProps}>
      <RenderIf condition={!params.hideTitle}>
        <Separator alignContent="start">Company-specific info</Separator>
      </RenderIf>
      {!params.metadata || !params.otherInfo ? (
        <></>
      ) : !params.metadata.tabs || params.metadata.tabs?.length === 0 ? (
        params.metadata?.fields.map((field: IField) => createField(field))
      ) : (
        <Pivot>
          {params.metadata.tabs
            ?.filter((tab: IFieldsTab) => !tab.hidden)
            .map((tab: IFieldsTab) => (
              <PivotItem headerText={tab.caption}>
                <Stack {...columnProps} styles={{ root: { paddingTop: 10 } }}>
                  {params.metadata?.fields
                    .filter((field: IField) => field.tabName === tab.tabName)
                    .map((field: IField) => createField(field))}
                </Stack>
              </PivotItem>
            ))}
        </Pivot>
      )}

      <MapPickDialog
        isModalOpen={isMapPickOpen}
        hideModal={hideMapPick}
        location={
          parseLatLng(otherInfoObj[currentField?.name ?? '']) ?? undefined
        }
        onMapClick={(newLocation: ILocation) => {
          handleOtherInfoChangeBase(currentField?.name ?? '', {
            latitude: newLocation.latitude,
            longitude: newLocation.longitude,
          });
          hideMapPick();
        }}
      />
    </Stack>
  );
};
export default OtherInfo;
